import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sendSmsCode, getMassageCityList, submitStaffApply, CityItem } from "@/api/staff";
import { useUserStore } from "@/store/useUserStore";

const BANNER_IMG = "/recruit-banner.png";

export default function RecruitPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userStore = useUserStore();

  // 从 URL 参数获取 spread（达人 uid）
  const spreadFromUrl = searchParams.get("spread") || "";
  const [spread, setSpread] = useState(spreadFromUrl);

  // 表单状态
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    code: "",
    gender: "2", // 默认女
    cityId: "",
    cityName: "",
    intro: "",
  });

  // 验证码倒计时
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 城市列表弹窗
  const [cityList, setCityList] = useState<CityItem[]>([]);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 加载城市列表
  const loadCityList = async () => {
    try {
      // 打印接口域名和完整路径
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "https://peiwan.banly.com.cn";
      const fullUrl = `${baseUrl}api/massage/city/list`;
      console.log("[城市列表接口]", {
        实际调用路径: "/massage/city/list",
        正确路径应为: "/staffapi/massage/city/list",
        完整接口: fullUrl,
      });

      const res = await getMassageCityList();
      console.log("[城市列表响应]", res.data);
      if (res.data) {
        setCityList(res.data);
        setShowCityPicker(true);
      }
    } catch (err: any) {
      console.error("[城市列表错误]", err);
      alert(err.message || "获取城市列表失败");
    }
  };

  // 选择城市
  const handleSelectCity = (city: CityItem) => {
    setFormData((prev) => ({
      ...prev,
      cityId: String(city.city_id),
      cityName: city.city_name,
    }));
    setShowCityPicker(false);
  };

  // 表单字段更新
  const updateField = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // 获取验证码
  const handleGetCode = async () => {
    console.log("[Recruit] handleGetCode called, phone:", formData.phone);
    
    if (!formData.phone.trim()) {
      alert("请输入手机号");
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
      alert("请输入正确的手机号");
      return;
    }
    if (sending) return;

    try {
      setSending(true);
      console.log("[Recruit] calling sendSmsCode...");
      await sendSmsCode(formData.phone, "apply_talent");
      console.log("[Recruit] sendSmsCode success");
      alert("验证码已发送");
      
      // 开始倒计时
      setCountdown(60);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      console.error("[Recruit] sendSmsCode error:", err);
      alert(err.message || "发送失败");
    } finally {
      setSending(false);
    }
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert("请输入联系人姓名");
      return;
    }
    if (!formData.phone.trim() || !/^1[3-9]\d{9}$/.test(formData.phone)) {
      alert("请输入正确的手机号");
      return;
    }
    if (!formData.code.trim()) {
      alert("请输入验证码");
      return;
    }
    if (!formData.cityId) {
      alert("请选择意向城市");
      return;
    }

    try {
      setSubmitting(true);

      const res = await submitStaffApply({
        link_mobile: formData.phone,
        link_name: formData.name,
        sex: formData.gender,
        city_id: formData.cityId,
        short_desc: formData.intro,
        work_time: "",
        captcha: formData.code,
        spread: spread, // 使用 URL 参数中的 spread（达人 uid）
      });

      // 使用接口返回的 msg 显示成功信息
      alert(res.msg);
      navigate("/home");
    } catch (err: any) {
      // 使用接口返回的 msg 显示失败信息
      alert(err?.response?.data?.msg || err.message || "提交失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: "#F5F5F5",
        fontFamily: "'PingFang SC','SF Pro Text','Inter',system-ui,sans-serif",
      }}
    >
      <div className="overflow-y-auto" style={{ height: "100vh" }}>
        {/* Banner 区域 */}
          <img
            src={BANNER_IMG}
            alt=""
            className="w-full block"
          />

        {/* 提示文字 */}
        <p className="px-4 py-3 text-sm text-gray-400">方便我们之后联系您</p>

        {/* 表单区域 */}
        <div className="px-4">
          <div className="bg-white rounded-xl overflow-hidden shadow-sm">
            {/* 联系人 */}
            <div className="flex items-center px-4 py-4 border-b border-gray-50">
              <div className="flex items-center gap-3 shrink-0">
                <i className="ri-user-line text-lg text-gray-600" />
                <span className="text-base text-gray-800 whitespace-nowrap">联系人</span>
              </div>
              <input
                type="text"
                placeholder="请输入联系人姓名"
                value={formData.name}
                onChange={(e) => updateField("name", e.target.value)}
                className="flex-1 text-sm text-right outline-none placeholder:text-gray-300 ml-3"
              />
            </div>

            {/* 联系人电话 */}
            <div className="flex items-center px-4 py-4 border-b border-gray-50">
              <div className="flex items-center gap-3 shrink-0">
                <i className="ri-phone-line text-lg text-gray-600" />
                <span className="text-base text-gray-800 whitespace-nowrap">联系人电话</span>
              </div>
              <input
                type="tel"
                placeholder="请输入联系人电话"
                maxLength={11}
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value.replace(/\D/g, ""))}
                className="flex-1 text-sm text-right outline-none placeholder:text-gray-300 ml-3"
              />
            </div>

            {/* 验证码 */}
            <div className="flex items-center px-4 py-4 border-b border-gray-50">
              <div className="flex items-center gap-3 shrink-0">
                <i className="ri-shield-keyhole-line text-lg text-gray-600" />
                <span className="text-base text-gray-800 whitespace-nowrap">验证码</span>
              </div>
              <input
                type="text"
                placeholder="请输入验证码"
                maxLength={6}
                value={formData.code}
                onChange={(e) => updateField("code", e.target.value)}
                className="flex-1 text-sm outline-none placeholder:text-gray-300 min-w-0 ml-3"
              />
              <button
                onClick={handleGetCode}
                disabled={
                  sending ||
                  countdown > 0 ||
                  !/^1[3-9]\d{9}$/.test(formData.phone)
                }
                className={`shrink-0 text-sm px-2 py-1 rounded ${
                  sending || countdown > 0 || !/^1[3-9]\d{9}$/.test(formData.phone)
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-[#FF7A3A] cursor-pointer"
                }`}
              >
                {countdown > 0 ? `${countdown}s` : sending ? "发送中..." : "获取验证码"}
              </button>
            </div>

            {/* 性别 */}
            <div className="flex items-center px-4 py-4 border-b border-gray-50">
              <div className="flex items-center gap-3 shrink-0">
                <i className="ri-user-settings-line text-lg text-gray-600" />
                <span className="text-base text-gray-800 whitespace-nowrap">性别</span>
              </div>
              <div className="ml-auto flex items-center gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="gender"
                    value="1"
                    checked={formData.gender === "1"}
                    onChange={(e) => updateField("gender", e.target.value)}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <span className="text-sm text-gray-700">男</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="gender"
                    value="2"
                    checked={formData.gender === "2"}
                    onChange={(e) => updateField("gender", e.target.value)}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <span className="text-sm text-gray-700">女</span>
                </label>
              </div>
            </div>

            {/* 意向城市 */}
            <button
              onClick={loadCityList}
              className="flex items-center px-4 py-4 border-b border-gray-50 w-full active:bg-gray-50"
            >
              <div className="flex items-center gap-3 shrink-0">
                <i className="ri-map-pin-line text-lg text-gray-600" />
                <span className="text-base text-gray-800 whitespace-nowrap">意向城市</span>
              </div>
              <span
                className={`flex-1 text-sm text-right ml-3 ${
                  formData.cityName ? "text-gray-800" : "text-gray-300"
                }`}
              >
                {formData.cityName || "请选择意向城市"}
              </span>
              <i className="ri-arrow-right-s-line text-gray-300" />
            </button>

            {/* 个人简介 */}
            <div className="px-4 py-4">
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-file-text-line text-lg text-gray-600" />
                <span className="text-base text-gray-800 whitespace-nowrap">个人简介</span>
              </div>
              <textarea
                placeholder="请输入个人简介"
                maxLength={200}
                value={formData.intro}
                onChange={(e) => updateField("intro", e.target.value)}
                rows={4}
                className="w-full bg-gray-50 rounded-lg px-3 py-3 text-sm outline-none resize-none placeholder:text-gray-300"
              />
              <div className="text-right text-xs text-gray-400 mt-1">
                {formData.intro.length}/200
              </div>
            </div>
          </div>
        </div>

        {/* 底部按钮区域 */}
        <div className="px-4 pt-8 pb-8">
          {/* 提交按钮 */}
          <button
            onClick={handleSubmit}
            disabled={
              submitting ||
              !formData.name ||
              !formData.phone ||
              !formData.code ||
              !formData.cityId
            }
            className="w-full h-12 rounded-full font-medium text-base text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{
              background: "linear-gradient(135deg, #FF7A3A, #FF9E5C)",
            }}
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                提交中...
              </>
            ) : (
              <>
                <i className="ri-edit-line" />
                提交
              </>
            )}
          </button>
        </div>

        {/* 城市选择抽屉 */}
        <div
          className={`fixed inset-0 z-50 transition-all duration-300 ${
            showCityPicker ? "visible" : "invisible"
          }`}
        >
          {/* 遮罩层 */}
          <div
            className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
              showCityPicker ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setShowCityPicker(false)}
          />
          {/* 抽屉内容 */}
          <div
            className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-hidden transition-transform duration-300 ${
              showCityPicker ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
              <span className="text-base font-medium text-gray-800">选择城市</span>
              <button
                onClick={() => setShowCityPicker(false)}
                className="text-gray-400 active:text-gray-600"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="overflow-y-auto p-4" style={{ maxHeight: "calc(70vh - 60px)" }}>
              {loading ? (
                <div className="text-center py-8 text-gray-400">加载中...</div>
              ) : cityList.length === 0 ? (
                <div className="text-center py-8 text-gray-400">暂无城市数据</div>
              ) : (
                <div className="space-y-2">
                  {cityList.map((city) => (
                    <button
                      key={city.city_id}
                      onClick={() => handleSelectCity(city)}
                      className={`w-full py-3 px-4 rounded-lg text-sm text-center transition-colors ${
                        formData.cityId === String(city.city_id)
                          ? "bg-[#FF7A3A] text-white"
                          : "bg-gray-100 text-gray-700 active:bg-gray-200"
                      }`}
                    >
                      {city.city_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
